import { FC, useState, ChangeEvent } from 'react';
import { Button, InputTextField } from '@/components';
import { useToastNotification } from '@/hooks';
import { validateEmail } from '@/utils/formValidations';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PagesPaths } from '@/enums';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { sendForgotPasswordEmail } from '../../sendForgotPasswordEmail';
import styles from './ResetPasswordForm.module.scss';

type ResetPasswordFormProps = {
  onReset: (userEmail: string) => void;
};

const ResetPasswordForm: FC<ResetPasswordFormProps> = ({ onReset }) => {
  const { t } = useTranslation('forgotPassword');
  const { showToast } = useToastNotification();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const resetPasswordMutation = useMutation({
    mutationFn: sendForgotPasswordEmail,
    onSuccess: () => onReset(email),
    onError: () => {
      showToast({ severity: 'error', detail: t('toastErrorText') });
    },
  });

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmailError('');
    setEmail(e.target.value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const emailErrorMessage = validateEmail(email, {
      required: t('emailRequired'),
      invalid: t('invalidEmail'),
    });

    if (emailErrorMessage) {
      setEmailError(emailErrorMessage);
      return;
    }

    resetPasswordMutation.mutate(email);
  };

  const submitButtonText = resetPasswordMutation.isPending
    ? t('loadingButton')
    : t('sendButton');

  return (
    <>
      <h1>{t('passwordReset')}</h1>
      <p>{t('instruction')}</p>
      <form onSubmit={handleSubmit}>
        <InputTextField
          id="email"
          name="email"
          placeholder={t('email')}
          label={t('email')}
          value={email}
          type="email"
          onChange={handleEmailChange}
          error={emailError}
        />
        <Button disabled={resetPasswordMutation.isPending} type="submit">
          {submitButtonText}
        </Button>
        <Link to={PagesPaths.Login} className={styles.backButton}>
          <FontAwesomeIcon icon={faChevronLeft} className={styles.backIcon} />
          {t('backButton')}
        </Link>
      </form>
    </>
  );
};

export default ResetPasswordForm;
